import React, { useContext, useState, useEffect } from 'react';
import moment from 'moment';
import classnames from 'classnames';
import {
  DayPickerRangeController,
  DateRangePicker,
  isInclusivelyAfterDay,
} from 'react-dates';
import {
  START_DATE,
  END_DATE,
  HORIZONTAL_ORIENTATION,
  VERTICAL_ORIENTATION,
} from 'react-dates/constants';
import Icon, { Icons } from '@shared/Icons/Icon';
import { useLocale } from '../../../../../hooks/useLocale';
import DiscoveryContext from '../../../../../contexts/discoveryContext/contextV2';
import { sortingCategories } from '../../../../../contexts/discoveryContext/utils/sortOptions';
import { toDateRangeStrings } from '../../../../../contexts/discoveryContext/utils/date';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import './styles.css';

import {
  monthsTitle,
  dateInputTitle,
  buttonContainer,
  dateButton,
  active,
} from './styles.module.css';

const getStartEndCalendarDates = (selectedDateIds, dateRanges) => {
  if (selectedDateIds.length) {
    return { start: null, end: null };
  }

  if (dateRanges.length !== 1) {
    return { start: null, end: null };
  }

  return { start: moment(dateRanges[0].min), end: moment(dateRanges[0].max) };
};

const DateSelect = ({ update, isMobile }) => {
  const {
    filters: {
      date: {
        dateRanges,
        availableDates,
        selectedDateIds: preSelectedSelectedDateIds,
      },
    },
  } = useContext(DiscoveryContext);
  const locale = useLocale();

  const [dates, setDates] = useState(
    availableDates.map((date) => ({
      ...date,
      selected: !!preSelectedSelectedDateIds.find((n) => n === date.id),
    })),
  );

  const {
    start: calendarDefaultStartDate,
    end: calendarDefaultEndDate,
  } = getStartEndCalendarDates(preSelectedSelectedDateIds, dateRanges);

  const [focusState, setFocusState] = useState(null);
  const [calendarStartDate, setCalendarStartDate] = useState(
    calendarDefaultStartDate,
  );
  const [calendarEndDate, setCalendarEndDate] = useState(
    calendarDefaultEndDate,
  );

  useEffect(() => {
    if (!calendarStartDate && !calendarEndDate) {
      update({
        sort: undefined,
        sortOption: undefined,
        selectedPopularDateIds: [],
        dateRanges: [],
      });
    }
  }, [calendarStartDate, calendarEndDate]);

  const onToggle = (id) => {
    const selectedDates = dates.map((date) => {
      const selected = date.id === id ? !date.selected : date.selected;
      return { ...date, selected };
    });

    const activeSelectedDate = selectedDates.filter((n) => n.selected);

    setDates(selectedDates);
    setCalendarStartDate(null);
    setCalendarEndDate(null);
    update({
      sort: activeSelectedDate.length ? { date: 'asc' } : undefined,
      sortOption: activeSelectedDate.length
        ? `${sortingCategories.DATE}-0`
        : undefined,
      selectedPopularDateIds: activeSelectedDate.map((n) => n.id),
      dateRanges: toDateRangeStrings(activeSelectedDate),
    });
  };

  const unToggleAll = () => {
    const untoggledDates = dates.map((date) => ({ ...date, selected: false }));

    setDates(untoggledDates);
  };

  const onDatesChange = ({ startDate, endDate }) => {
    setCalendarStartDate(startDate);
    setCalendarEndDate(endDate);

    if (!startDate || !endDate) {
      return;
    }

    unToggleAll();

    update({
      sort: { date: 'asc' },
      sortOption: `${sortingCategories.DATE}-0`,
      selectedPopularDateIds: [],
      dateRanges: toDateRangeStrings([
        { min: startDate.toISOString(), max: endDate.toISOString() },
      ]),
    });
  };

  const calendarProps = {
    startDate: calendarStartDate,
    endDate: calendarEndDate,
    onDatesChange,
    focusedInput: focusState,
    onFocusChange: (focusedInput) => {
      setFocusState(focusedInput);
      document.activeElement.blur();
    },
    initialVisibleMonth: null,
    numberOfMonths: 2,
    hideKeyboardShortcutsPanel: true,
    noBorder: true,
    daySize: 34,
    orientation: isMobile ? VERTICAL_ORIENTATION : HORIZONTAL_ORIENTATION,
    minDate: moment.utc(),
    isOutsideRange: (day) => !isInclusivelyAfterDay(day, moment()),
  };

  return (
    <>
      <span className={monthsTitle}>Select whole months:</span>
      <div className={buttonContainer}>
        {dates.map((date) => {
          const { id, label, selected } = date;

          return (
            <div
              key={id}
              className={classnames(dateButton, { [active]: selected })}
              onClick={() => onToggle(id)}
              onKeyPress={() => onToggle(id)}
            >
              <span>{label}</span>
            </div>
          );
        })}
      </div>

      {isMobile && (
        <span className={dateInputTitle}>Or, select specific dates:</span>
      )}

      {isMobile ? (
        <DateRangePicker
          {...calendarProps}
          startDatePlaceholderText="From"
          endDatePlaceholderText="To"
          startDateId={START_DATE}
          endDateId={END_DATE}
          customArrowIcon={<></>}
          customInputIcon={<Icon icon={Icons.CALENDAR.RED} />}
          displayFormat={locale === 'us' ? 'MM/DD/YY' : 'DD/MM/YY'}
          withFullScreenPortal
          showClearDates
          block
        />
      ) : (
        <DayPickerRangeController
          {...calendarProps}
          focusedInput={focusState || START_DATE}
        />
      )}
    </>
  );
};

export default DateSelect;
